import React, { useState, useCallback } from "react";
import { haProxyJson, haProxyRequest } from "./haProxy";

/* ══ SMART HOME AUTOMATIONS ═══════════════════════════════
   List, toggle, manually trigger, and delete HA automations.
   Template-based creator for common automation patterns.

   HA APIs used (direct browser → HA):
   - Service: automation.turn_on | turn_off | trigger | reload
   - Service: automation.remove
   - REST: GET/POST/DELETE /api/config/automation/config/{id}
     (creates new automation file)

   Not included in V2: full YAML editor (needs syntax
   highlighting lib), conditions, complex triggers, Jinja
   template editing. Build YAML via template creator instead. */

const AUTOMATION_TEMPLATES = [
  { id: "state_scene",    label: "When device changes state, activate scene", labelAr: "عند تغيير الجهاز، فعّل مشهد" },
  { id: "time_scene",     label: "At a specific time, activate scene",        labelAr: "في وقت محدد، فعّل مشهد" },
  { id: "threshold",      label: "When sensor crosses threshold, run action", labelAr: "عند تجاوز المستشعر حداً، نفّذ إجراء" },
];

function SmartHomeAutomations({ entities, scenes, locId, callService, canControl, isRTL }) {
  const automations = entities.filter(e => e.domain === "automation");
  const [yamlView, setYamlView] = useState(null); /* automation being viewed */
  const [yaml, setYaml] = useState(null);
  const [loadingYaml, setLoadingYaml] = useState(false);
  const [yamlError, setYamlError] = useState("");
  const [showBuilder, setShowBuilder] = useState(false);

  const openYaml = useCallback(async (automation) => {
    setYamlView(automation);
    setLoadingYaml(true);
    setYamlError("");
    setYaml(null);

    /* Strip "automation." prefix to get the numeric/slug id */
    const autoId = automation.attributes?.id || automation.entity_id.replace(/^automation\./, "");
    try {
      const cfg = await haProxyJson({
        locId,
        path: `/api/config/automation/config/${autoId}`,
      });
      setYaml(cfg);
    } catch (err) {
      if (err.status === 404) {
        setYamlError(isRTL
          ? "هذه الأتمتة مُعرّفة في configuration.yaml ولا يمكن عرضها عبر API"
          : "This automation is defined in configuration.yaml and cannot be viewed via the API.");
      } else {
        setYamlError(err.message);
      }
    } finally {
      setLoadingYaml(false);
    }
  }, [locId, isRTL]);

  async function toggleAutomation(automation) {
    const on = automation.state === "on";
    await callService("automation", on ? "turn_off" : "turn_on", automation.entity_id);
  }

  async function triggerAutomation(automation) {
    await callService("automation", "trigger", automation.entity_id);
  }

  async function deleteAutomation(automation) {
    if (!window.confirm(
      isRTL
        ? `حذف الأتمتة "${automation.friendly_name}"؟`
        : `Delete automation "${automation.friendly_name}"?`
    )) return;

    const autoId = automation.attributes?.id || automation.entity_id.replace(/^automation\./, "");
    try {
      const res = await haProxyRequest({
        locId,
        method: "DELETE",
        path: `/api/config/automation/config/${autoId}`,
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.error || `HA returned ${res.status}`);
      }
      /* Reload automations in HA */
      await callService("automation", "reload");
    } catch (err) {
      alert((isRTL ? "فشل الحذف: " : "Delete failed: ") + err.message);
    }
  }

  /* ── YAML detail view ── */
  if (yamlView) {
    return (
      <div className="sh-auto-yaml">
        <div className="sh-room-header">
          <button className="sh-room-back" onClick={() => { setYamlView(null); setYaml(null); setYamlError(""); }}>
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"><path d={isRTL ? "M6 3l5 5-5 5" : "M10 3L5 8l5 5"}/></svg>
          </button>
          <div style={{ flex: 1 }}>
            <h2 className="sh-room-title">{yamlView.friendly_name}</h2>
            <p className="sh-room-sub">{isRTL ? "تعريف الأتمتة (للقراءة فقط)" : "Automation config (read-only)"}</p>
          </div>
        </div>

        {loadingYaml && <p style={{ textAlign: "center", color: "var(--g400)", padding: "2rem" }}>{isRTL ? "جارٍ التحميل..." : "Loading..."}</p>}
        {yamlError && <div className="sh-error">{yamlError}</div>}
        {yaml && (
          <pre className="sh-yaml-block">
            {JSON.stringify(yaml, null, 2)}
          </pre>
        )}
      </div>
    );
  }

  /* ── Builder modal ── */
  if (showBuilder) {
    return (
      <AutomationBuilder
        entities={entities}
        scenes={scenes}
        locId={locId}
        callService={callService}
        isRTL={isRTL}
        onClose={() => setShowBuilder(false)}
      />
    );
  }

  return (
    <div className="sh-autos">
      <div className="sh-autos-header">
        <p className="sh-notif-desc" style={{ margin: 0, flex: 1 }}>
          {isRTL
            ? `${automations.length} أتمتة`
            : `${automations.length} automation${automations.length !== 1 ? "s" : ""}`}
        </p>
        {canControl && (
          <button className="btn btn-p" onClick={() => setShowBuilder(true)}>
            + {isRTL ? "إضافة" : "Create"}
          </button>
        )}
      </div>

      {automations.length === 0 ? (
        <div className="sh-scenes-empty">
          <svg width="40" height="40" viewBox="0 0 40 40" fill="none" stroke="var(--g300)" strokeWidth="1.3">
            <circle cx="20" cy="20" r="14"/><path d="M14 20l4 4 8-8"/>
          </svg>
          <p>{isRTL ? "لا توجد أتمتات" : "No automations found."}</p>
          <p className="sh-scenes-hint">
            {isRTL ? "أنشئ أتمتة جديدة أو أضف واحدة في HA" : "Create one or add automations in HA."}
          </p>
        </div>
      ) : (
        <div className="sh-auto-list">
          {automations.map(a => {
            const on = a.state === "on";
            const last = a.attributes?.last_triggered;
            return (
              <div key={a.entity_id} className={`sh-auto-row${on ? " sh-auto-on" : ""}`}>
                <div className="sh-auto-info">
                  <div className="sh-auto-name">{a.friendly_name}</div>
                  <div className="sh-auto-meta">
                    {last
                      ? (isRTL ? `آخر تشغيل: ${new Date(last).toLocaleString("ar-SA")}` : `Last: ${new Date(last).toLocaleString("en-GB", { dateStyle: "short", timeStyle: "short" })}`)
                      : (isRTL ? "لم تُشغَّل بعد" : "Never triggered")}
                  </div>
                </div>
                <div className="sh-auto-actions">
                  <button className="sh-auto-ibtn" title={isRTL ? "عرض" : "View"} onClick={() => openYaml(a)}>
                    <svg width="14" height="14" viewBox="0 0 14 14" fill="none" stroke="currentColor" strokeWidth="1.3"><path d="M1 7s2.5-4.5 6-4.5S13 7 13 7s-2.5 4.5-6 4.5S1 7 1 7z"/><circle cx="7" cy="7" r="2"/></svg>
                  </button>
                  {canControl && (
                    <>
                      <button className="sh-auto-ibtn" title={isRTL ? "تشغيل يدوي" : "Trigger"} onClick={() => triggerAutomation(a)}>
                        <svg width="14" height="14" viewBox="0 0 14 14" fill="none" stroke="currentColor" strokeWidth="1.3"><path d="M3 2l8 5-8 5V2z"/></svg>
                      </button>
                      <button className="sh-auto-ibtn" title={isRTL ? "حذف" : "Delete"} onClick={() => deleteAutomation(a)}>
                        <svg width="14" height="14" viewBox="0 0 14 14" fill="none" stroke="currentColor" strokeWidth="1.3"><path d="M3 3.5h8M5 3.5V2h4v1.5M4 3.5v8h6v-8"/></svg>
                      </button>
                      <button className={`sh-toggle${on ? " sh-toggle-on" : ""}`} onClick={() => toggleAutomation(a)}>
                        <div className="sh-toggle-knob" />
                      </button>
                    </>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

/* ══ Automation Builder — template-based creator ══ */
function AutomationBuilder({ entities, scenes, locId, callService, isRTL, onClose }) {
  const [template, setTemplate] = useState("state_scene");
  const [name, setName] = useState("");
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);

  /* Template-specific state */
  const [stateEntity, setStateEntity] = useState("");
  const [stateValue, setStateValue] = useState("");
  const [sceneId, setSceneId] = useState("");
  const [time, setTime] = useState("18:00");
  const [days, setDays] = useState({ mon: true, tue: true, wed: true, thu: true, fri: true, sat: true, sun: true });
  const [sensorEntity, setSensorEntity] = useState("");
  const [operator, setOperator] = useState("above");
  const [threshold, setThreshold] = useState("");
  const [actionEntity, setActionEntity] = useState("");
  const [actionService, setActionService] = useState("turn_on");

  /* Entity options filtered by domain */
  const controllable = entities.filter(e =>
    ["light", "switch", "lock", "fan", "cover", "climate"].includes(e.domain)
  );
  const sensors = entities.filter(e => e.domain === "sensor" || e.domain === "binary_sensor");

  function buildConfig() {
    const id = `cmd_${Date.now()}`;
    const alias = name.trim() || (isRTL ? "أتمتة جديدة" : "New Automation");

    if (template === "state_scene") {
      if (!stateEntity || !stateValue || !sceneId) {
        setError(isRTL ? "اختر الجهاز والحالة والمشهد" : "Select entity, state, and scene.");
        return null;
      }
      return {
        id, alias, description: "",
        trigger: [{ platform: "state", entity_id: stateEntity, to: stateValue }],
        action: [{ service: "scene.turn_on", target: { entity_id: sceneId } }],
        mode: "single",
      };
    }

    if (template === "time_scene") {
      if (!time || !sceneId) {
        setError(isRTL ? "اختر الوقت والمشهد" : "Select time and scene.");
        return null;
      }
      const dayList = Object.entries(days).filter(([, v]) => v).map(([k]) => k);
      if (dayList.length === 0) {
        setError(isRTL ? "اختر يوماً واحداً على الأقل" : "Select at least one day.");
        return null;
      }
      return {
        id, alias, description: "",
        trigger: [{ platform: "time", at: time }],
        condition: [{ condition: "time", weekday: dayList }],
        action: [{ service: "scene.turn_on", target: { entity_id: sceneId } }],
        mode: "single",
      };
    }

    if (template === "threshold") {
      if (!sensorEntity || !threshold || !actionEntity) {
        setError(isRTL ? "أكمل جميع الحقول" : "Complete all fields.");
        return null;
      }
      return {
        id, alias, description: "",
        trigger: [{
          platform: "numeric_state",
          entity_id: sensorEntity,
          [operator]: parseFloat(threshold),
        }],
        action: [{ service: `${actionEntity.split(".")[0]}.${actionService}`, target: { entity_id: actionEntity } }],
        mode: "single",
      };
    }
    return null;
  }

  async function save() {
    setError("");
    const cfg = buildConfig();
    if (!cfg) return;

    setSaving(true);
    try {
      const res = await haProxyRequest({
        locId,
        method: "POST",
        path: `/api/config/automation/config/${cfg.id}`,
        body: cfg,
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.error || `HA returned ${res.status} — the token may not have admin rights`);
      }
      /* Reload automations so the new one appears */
      await callService("automation", "reload");
      onClose();
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  }

  const DAYS_LIST = [
    { key: "mon", en: "Mon", ar: "اث" },
    { key: "tue", en: "Tue", ar: "ثل" },
    { key: "wed", en: "Wed", ar: "أر" },
    { key: "thu", en: "Thu", ar: "خم" },
    { key: "fri", en: "Fri", ar: "جم" },
    { key: "sat", en: "Sat", ar: "سب" },
    { key: "sun", en: "Sun", ar: "أح" },
  ];

  return (
    <div className="sh-auto-builder">
      <div className="sh-room-header">
        <button className="sh-room-back" onClick={onClose}>
          <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"><path d={isRTL ? "M6 3l5 5-5 5" : "M10 3L5 8l5 5"}/></svg>
        </button>
        <div>
          <h2 className="sh-room-title">{isRTL ? "إنشاء أتمتة" : "Create Automation"}</h2>
          <p className="sh-room-sub">{isRTL ? "اختر نمطاً" : "Pick a template"}</p>
        </div>
      </div>

      {/* Template picker */}
      <div className="sh-type-cards">
        {AUTOMATION_TEMPLATES.map(tpl => (
          <button key={tpl.id} className={`sh-type-card${template === tpl.id ? " sh-type-active" : ""}`} onClick={() => setTemplate(tpl.id)}>
            <div className="sh-type-radio">{template === tpl.id && <div className="sh-type-radio-dot" />}</div>
            <div>
              <strong>{isRTL ? tpl.labelAr : tpl.label}</strong>
            </div>
          </button>
        ))}
      </div>

      {/* Name */}
      <div className="sh-form">
        <label className="fl">{isRTL ? "اسم الأتمتة" : "Automation name"}</label>
        <input className="fi" type="text" value={name} onChange={e => setName(e.target.value)} placeholder={isRTL ? "مثلاً: أضواء المساء" : "e.g. Evening Lights"} />

        {/* Template: State → Scene */}
        {template === "state_scene" && (
          <>
            <label className="fl" style={{ marginTop: 12 }}>{isRTL ? "الجهاز" : "Entity"}</label>
            <select className="fi" value={stateEntity} onChange={e => { setStateEntity(e.target.value); setStateValue(""); }}>
              <option value="">{isRTL ? "اختر..." : "Select..."}</option>
              {controllable.map(e => <option key={e.entity_id} value={e.entity_id}>{e.friendly_name}</option>)}
            </select>

            <label className="fl" style={{ marginTop: 12 }}>{isRTL ? "عندما تصبح الحالة" : "Changes to state"}</label>
            <select className="fi" value={stateValue} onChange={e => setStateValue(e.target.value)}>
              <option value="">{isRTL ? "اختر..." : "Select..."}</option>
              <option value="on">on</option>
              <option value="off">off</option>
              <option value="open">open</option>
              <option value="closed">closed</option>
              <option value="locked">locked</option>
              <option value="unlocked">unlocked</option>
            </select>

            <label className="fl" style={{ marginTop: 12 }}>{isRTL ? "فعّل مشهد" : "Activate scene"}</label>
            <select className="fi" value={sceneId} onChange={e => setSceneId(e.target.value)}>
              <option value="">{isRTL ? "اختر..." : "Select..."}</option>
              {scenes.map(s => <option key={s.entity_id} value={s.entity_id}>{s.friendly_name}</option>)}
            </select>
          </>
        )}

        {/* Template: Time → Scene */}
        {template === "time_scene" && (
          <>
            <label className="fl" style={{ marginTop: 12 }}>{isRTL ? "الوقت" : "Time"}</label>
            <input className="fi" type="time" value={time} onChange={e => setTime(e.target.value)} dir="ltr" />

            <label className="fl" style={{ marginTop: 12 }}>{isRTL ? "الأيام" : "Days"}</label>
            <div className="sh-auto-days">
              {DAYS_LIST.map(d => (
                <button
                  key={d.key}
                  className={`sh-mode-pill${days[d.key] ? " sh-mode-active" : ""}`}
                  onClick={() => setDays(prev => ({ ...prev, [d.key]: !prev[d.key] }))}
                >
                  {isRTL ? d.ar : d.en}
                </button>
              ))}
            </div>

            <label className="fl" style={{ marginTop: 12 }}>{isRTL ? "فعّل مشهد" : "Activate scene"}</label>
            <select className="fi" value={sceneId} onChange={e => setSceneId(e.target.value)}>
              <option value="">{isRTL ? "اختر..." : "Select..."}</option>
              {scenes.map(s => <option key={s.entity_id} value={s.entity_id}>{s.friendly_name}</option>)}
            </select>
          </>
        )}

        {/* Template: Threshold */}
        {template === "threshold" && (
          <>
            <label className="fl" style={{ marginTop: 12 }}>{isRTL ? "المستشعر" : "Sensor"}</label>
            <select className="fi" value={sensorEntity} onChange={e => setSensorEntity(e.target.value)}>
              <option value="">{isRTL ? "اختر..." : "Select..."}</option>
              {sensors.map(e => <option key={e.entity_id} value={e.entity_id}>{e.friendly_name}</option>)}
            </select>

            <label className="fl" style={{ marginTop: 12 }}>{isRTL ? "عندما القيمة" : "When value is"}</label>
            <div style={{ display: "flex", gap: 8 }}>
              <select className="fi" style={{ flex: "0 0 100px" }} value={operator} onChange={e => setOperator(e.target.value)}>
                <option value="above">{isRTL ? "أعلى من" : "above"}</option>
                <option value="below">{isRTL ? "أقل من" : "below"}</option>
              </select>
              <input className="fi" style={{ flex: 1 }} type="number" value={threshold} onChange={e => setThreshold(e.target.value)} placeholder="e.g. 25" />
            </div>

            <label className="fl" style={{ marginTop: 12 }}>{isRTL ? "نفّذ إجراء على" : "Run action on"}</label>
            <select className="fi" value={actionEntity} onChange={e => setActionEntity(e.target.value)}>
              <option value="">{isRTL ? "اختر..." : "Select..."}</option>
              {controllable.map(e => <option key={e.entity_id} value={e.entity_id}>{e.friendly_name}</option>)}
            </select>

            <label className="fl" style={{ marginTop: 12 }}>{isRTL ? "الإجراء" : "Service"}</label>
            <select className="fi" value={actionService} onChange={e => setActionService(e.target.value)}>
              <option value="turn_on">turn_on</option>
              <option value="turn_off">turn_off</option>
              <option value="toggle">toggle</option>
            </select>
          </>
        )}

        {error && <div className="sh-error" style={{ marginTop: 12 }}>{error}</div>}
      </div>

      <div className="sh-btn-row" style={{ marginTop: 16 }}>
        <button className="btn btn-s" onClick={onClose}>{isRTL ? "إلغاء" : "Cancel"}</button>
        <button className="btn btn-p" disabled={saving} onClick={save}>
          {saving ? (isRTL ? "جارٍ الحفظ..." : "Saving...") : (isRTL ? "حفظ الأتمتة" : "Create Automation")}
        </button>
      </div>
    </div>
  );
}

export default SmartHomeAutomations;
